import { Col, Row } from "react-bootstrap";
import { CountryData } from "react-phone-input-2";
import styled, { css } from "styled-components";
import { Address } from "../../client/product/useTransaction";
import DropDown from "../../components/DropDown";
import Input from "../../components/Input";
import InputPhone from "../../components/InputPhone";
import Countries from "../../data/countries.json";

type PaymentFormProps = {
  data: Address;
  errors: { [key: string]: string };
  onChange: (newUpdate: { [key: string]: string }) => void;
  onError: (newErrors: { [key: string]: string }) => void;
};

const BlurBackground = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.25);
  filter: blur(2px);
  -webkit-filter: blur(2px);
`;

const FormWrapper = styled(Row)`
  position: relative;
  z-index: 1;

  width: 80%;
  padding: 24px;
  border-radius: 8px;
  &::before {
    content: "";
    z-index: -1;
    ${BlurBackground}
  }

  @media (max-width: 768px) {
    padding: 12px;
  }

  @media (max-width: 576px) {
    width: 100%;
  }
`;

function PaymentForm(props: PaymentFormProps) {
  const { data, errors, onChange, onError } = props;
  const countryItems = Countries.map((country) => ({
    label: country.name,
    value: country.countryCode,
  }));

  const handleFirstNameChanged = (firstName: string) => {
    if (!firstName) {
      onError({ firstName: "First name is required" });
    } else {
      onError({ firstName: "" });
    }
    onChange({ firstName });
  };

  const handleLastNameChanged = (lastName: string) => {
    if (!lastName) {
      onError({ lastName: "Last name is required" });
    } else {
      onError({ lastName: "" });
    }
    onChange({ lastName });
  };

  const handleEmailChanged = (email: string) => {
    // eslint-disable-next-line
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    if (!email) {
      onError({ email: "Email is required" });
    } else if (!emailRegex.test(email)) {
      onError({ email: "Email is not valid" });
    } else {
      onError({ email: "" });
    }
    onChange({ email });
  };

  const handleAddress1Changed = (address1: string) => {
    if (!address1) {
      onError({ address1: "Address 1 is required" });
    } else {
      onError({ address1: "" });
    }
    onChange({ address1 });
  };

  const handleAddress2Changed = (address2: string) => {
    onChange({ address2 });
  };

  const handleCountryChanged = (country: string) => {
    if (!country) {
      onError({ country: "Country is required" });
    } else {
      onError({ country: "" });
    }
    onChange({ country });
  };

  const handleRegionChanged = (region: string) => {
    if (!region) {
      onError({ region: "Region is required" });
    } else {
      onError({ region: "" });
    }
    onChange({ region });
  };

  const handlePincodeChanged = (pincode: string) => {
    if (!pincode) {
      onError({ pincode: "Pincode is required" });
    } else {
      onError({ pincode: "" });
    }
    onChange({ pincode });
  };

  const handlePhoneChanged = (phone: string) => {
    if (!phone) {
      onError({ phone: "" });
    } else if (phone.length < 6) {
      onError({ phone: "Phone is not valid" });
    } else {
      onError({ phone: "" });
    }
    onChange({
      phone,
    });
  };

  return (
    <FormWrapper className="mb-5">
      <Col md={6} className="mb-3">
        <Input
          label="First Name*"
          value={data.firstName}
          placeholder="First Name"
          onChange={handleFirstNameChanged}
          error={errors.firstName}
        />
      </Col>
      <Col md={6} className="mb-3">
        <Input
          label="Last Name*"
          value={data.lastName}
          placeholder="Last Name"
          onChange={handleLastNameChanged}
          error={errors.lastName}
        />
      </Col>
      <Col md={12} className="mb-3">
        <Input
          label="Email*"
          value={data.email}
          placeholder="Email"
          onChange={handleEmailChanged}
          error={errors.email}
        />
      </Col>
      <Col xs={12} className="mb-3">
        <Input
          label="Address 1*"
          value={data.address1}
          placeholder="Address"
          onChange={handleAddress1Changed}
          error={errors.address1}
        />
      </Col>
      <Col xs={12} className="mb-3">
        <Input
          label="Address 2"
          value={data.address2}
          placeholder="Address"
          onChange={handleAddress2Changed}
          error={errors.address2}
        />
      </Col>
      <Col xs={12} sm={6} className="mb-3">
        <DropDown
          label="Country*"
          items={countryItems}
          value={data.country}
          placeholder="Country"
          onChange={handleCountryChanged}
          error={errors.country}
        />
      </Col>
      <Col xs={12} sm={6} className="mb-3">
        <Input
          label="State / Region*"
          value={data.region}
          placeholder="State / Region"
          onChange={handleRegionChanged}
          error={errors.region}
        />
      </Col>
      <Col md={6} className="mb-3">
        <Input
          label="Zip Code / Postal Code*"
          value={data.pincode}
          placeholder="Postal Code"
          onChange={handlePincodeChanged}
          error={errors.pincode}
        />
      </Col>
      <Col md={12} className="mb-3">
        <InputPhone
          label="Phone"
          value={data.phone}
          placeholder="Phone"
          onChange={handlePhoneChanged}
          error={errors.phone}
        />
      </Col>
    </FormWrapper>
  );
}

export default PaymentForm;
